"use client";

import { useRef, useEffect } from "react";
import { Box } from "@mui/material";
import { useInView } from "framer-motion";
import { theme } from "@/lib/theme";
import { PageContainer } from "@/components/PageContainer";

interface ChapterProps {
  id: string;
  children: React.ReactNode;
  onInView?: (id: string) => void;
  backgroundColor?: string;
  useGlobalBackground?: boolean; // When true, background is transparent to show global color
}

export const Chapter = ({
  id,
  children,
  onInView,
  backgroundColor = theme.colors.white,
  useGlobalBackground = false,
}: ChapterProps) => {
  const ref = useRef<HTMLDivElement>(null);
  const isInView = useInView(ref, { amount: 0.3 });

  useEffect(() => {
    if (isInView && onInView) {
      onInView(id);
    }
  }, [isInView, id, onInView]);

  return (
    <Box
      id={id}
      ref={ref}
      component="section"
      sx={{
        paddingTop: {
          xs: 8, // 64px mobile
          sm: 10, // 80px desktop
        },
        paddingBottom: 0,
        background: useGlobalBackground
          ? "transparent"
          : id === "chapter-00"
            ? `linear-gradient(to bottom, ${theme.colors.lightLime}, ${theme.colors.darkLimeGreen})`
            : backgroundColor,
        position: "relative",
      }}
    >
      <PageContainer>
        <Box
          sx={{
            "& code": {
              backgroundColor: theme.colors.gray[50],
              padding: "2px 6px",
              borderRadius: theme.borderRadius.sm,
              fontSize: "0.9em",
              fontFamily: "monospace",
            },
            "& pre": {
              backgroundColor: "#1e1e1e",
              color: "#d4d4d4",
              padding: 3, // 24px
              borderRadius: theme.borderRadius.md,
              overflow: "auto",
              marginY: {
                xs: 3, // 24px mobile
                sm: 4, // 32px desktop
              },
              "& code": {
                backgroundColor: "transparent",
                padding: 0,
                color: "inherit",
                fontSize: "inherit",
              },
            },
            "& ul, & ol": {
              marginBottom: {
                xs: 3, // 24px mobile
                sm: 5, // 40px desktop
              },
              paddingLeft: {
                xs: 3, // 24px mobile
                sm: 5, // 40px desktop
              },
            },
            "& li": {
              marginBottom: 1, // 8px
            },
            "& img": {
              maxWidth: "100%",
              height: "auto",
              borderRadius: theme.borderRadius.md,
            },
          }}
        >
          {children}
        </Box>
      </PageContainer>
    </Box>
  );
};
